import {
  PLAYABLE,
  PLAYABLE_PROGRAMMES,
  EVENTS,
  formatMark,
  NAME_MAX,
  type EventDef,
} from '@decathlon/shared';
import { clear, EVENT_EMOJI, h, keyLabel } from '../dom.js';
import { BINDINGS, saveCustomBinding, validRunCode, type KeyBinding } from '../controls.js';
import { KIT_COLOURS } from '../game/blocky.js';
import { VIEW_MODES, type ViewMode } from '../game/scene.js';
import { MUSIC_TRACKS, type MusicTrack } from '../audio.js';
import type { PersonalBests } from '../pbs.js';

export { PLAYABLE, PLAYABLE_PROGRAMMES } from '@decathlon/shared';

export interface MenuModel {
  roomCode: string;
  roomError: string;
  name: string;
  account: string | null;
  authMode: 'none' | 'login' | 'register';
  authBusy: boolean;
  authPassword: string;
  authMessage: string;
  authError: boolean;
  kit: string;
  binding: KeyBinding;
  view: ViewMode;
  musicOn: boolean;
  music: MusicTrack;
  sfxOn: boolean;
  paOn: boolean;
  pbs: PersonalBests;
  resultCount: number;
  /** Practice: fill the lanes with bots. */
  practiceBots: boolean;
}

export interface MenuActions {
  practice(ev: EventDef | 'day1' | 'day2' | 'full'): void;
  playOnline(): void;
  createRoom(): void;
  joinRoom(): void;
  setRoomCode(code: string): void;
  setPracticeBots(on: boolean): void;
  setName(n: string): void;
  setAuthMode(m: 'none' | 'login' | 'register'): void;
  setAuthPassword(password: string): void;
  submitAuth(name: string, password: string): void;
  signOut(): void;
  showStats(): void;
  showLeague(): void;
  setKit(hex: string): void;
  setBinding(id: string): void;
  setView(v: ViewMode): void;
  setMusicOn(on: boolean): void;
  setMusic(t: MusicTrack): void;
  setSfx(on: boolean): void;
  setPa(on: boolean): void;
  about(): void;
}

export function renderMenu(root: HTMLElement, m: MenuModel, a: MenuActions): void {
  clear(root);
  const eventCard = (ev: EventDef) => {
    const pb = m.pbs[ev.id];
    const playable = PLAYABLE.has(ev.id);
    return h(
      'button',
      {
        class: `event-card${playable ? '' : ' soon'}`,
        disabled: !playable,
        onclick: () => a.practice(ev),
      },
      h('span', { class: 'emoji' }, EVENT_EMOJI[ev.id] ?? '🏅'),
      h('span', { class: 'ev-name' }, ev.name),
      h(
        'span',
        { class: 'ev-pb muted' },
        playable ? (pb !== undefined ? `PB ${formatMark(ev, pb)}` : 'No PB yet') : 'Coming soon',
      ),
    );
  };
  const day = (n: 1 | 2) =>
    h(
      'div',
      { class: 'day' },
      h('h3', {}, `Day ${n}`),
      h('div', { class: 'events' }, EVENTS.filter((e) => e.day === n).map(eventCard)),
    );

  // Name and account, like Typing Racer: a guest name, or sign in to keep progress.
  const input = h('input', {
    type: 'text',
    placeholder: 'Your name',
    maxlength: NAME_MAX,
    value: m.account ?? m.name,
    disabled: !!m.account || m.authBusy,
    autocomplete: 'nickname',
    'aria-label': 'Your name',
    id: 'athlete-name',
  }) as HTMLInputElement;
  input.addEventListener('input', () => a.setName(input.value));
  let account: HTMLElement;
  if (m.account) {
    account = h(
      'div',
      { class: 'account row', style: 'justify-content:space-between' },
      h('span', {}, '👤 Signed in as ', h('b', {}, m.account)),
      h(
        'span',
        { class: 'row' },
        h('button', { class: 'btn small blue', onclick: () => a.showStats() }, '📈 Progress'),
        h('button', { class: 'btn small ghost', onclick: () => a.signOut() }, 'Sign out'),
      ),
    );
  } else if (m.authMode === 'none') {
    account = h(
      'div',
      { class: 'account row', style: 'justify-content:space-between' },
      h(
        'span',
        { class: 'muted small' },
        m.resultCount
          ? `${m.resultCount} result${m.resultCount === 1 ? '' : 's'} saved in this browser.`
          : 'Guest: progress is saved in this browser only.',
      ),
      h(
        'span',
        { class: 'row' },
        m.resultCount
          ? h('button', { class: 'btn small blue', onclick: () => a.showStats() }, '📈 Progress')
          : null,
        h('button', { class: 'btn small ghost', onclick: () => a.setAuthMode('login') }, 'Sign in'),
        h(
          'button',
          { class: 'btn small primary', onclick: () => a.setAuthMode('register') },
          'Create account',
        ),
      ),
    );
  } else {
    const pass = h('input', {
      type: 'password',
      value: m.authPassword,
      disabled: m.authBusy,
      placeholder: m.authMode === 'register' ? 'Choose a password (6+ characters)' : 'Password',
      autocomplete: m.authMode === 'register' ? 'new-password' : 'current-password',
      'aria-label': 'Password',
    }) as HTMLInputElement;
    pass.addEventListener('input', () => a.setAuthPassword(pass.value));
    const submit = () => a.submitAuth(input.value, pass.value);
    pass.addEventListener('keydown', (e) => e.key === 'Enter' && submit());
    account = h(
      'div',
      { class: 'account stack' },
      h('b', {}, m.authMode === 'register' ? 'Create an account' : 'Sign in'),
      h(
        'div',
        { class: 'muted small' },
        m.authMode === 'register'
          ? 'Your name becomes yours alone, and every result is tracked so you can see your progress on any device.'
          : 'Enter the name and password you registered with.',
      ),
      pass,
      h(
        'div',
        { class: 'row' },
        h(
          'button',
          { class: 'btn small primary', disabled: m.authBusy, onclick: submit },
          m.authMode === 'register' ? 'Create account' : 'Sign in',
        ),
        h(
          'button',
          {
            class: 'btn small ghost',
            disabled: m.authBusy,
            onclick: () => a.setAuthMode(m.authMode === 'register' ? 'login' : 'register'),
          },
          m.authMode === 'register' ? 'I have an account' : 'Create one instead',
        ),
        h(
          'button',
          { class: 'btn small ghost', disabled: m.authBusy, onclick: () => a.setAuthMode('none') },
          'Cancel',
        ),
      ),
    );
  }

  root.append(
    h(
      'div',
      { class: 'center' },
      h(
        'div',
        { class: 'lobby' },
        h(
          'div',
          { class: 'panel stack' },
          h('h1', {}, '🏃 Deca', h('span', { class: 'accent' }, 'thlon')),
          h(
            'p',
            { class: 'muted' },
            'Hammer two keys to run. Same key twice does nothing: you must alternate. Ten events, three lives, real IAAF points.',
          ),
          h('label', { for: 'athlete-name' }, m.account ? 'Your athlete' : 'Enter your name'),
          input,
          account,
          m.authMessage
            ? h(
                'p',
                {
                  class: m.authError ? 'auth-error' : 'auth-success',
                  role: m.authError ? 'alert' : 'status',
                },
                m.authMessage,
              )
            : null,
          h('h2', {}, 'Play online'),
          h(
            'div',
            { class: 'row' },
            h(
              'button',
              { class: 'btn primary', onclick: () => a.playOnline() },
              '🌐 Join public room',
            ),
            h('button', { class: 'btn blue', onclick: () => a.createRoom() }, '🔗 Create room'),
          ),
          h(
            'form',
            {
              class: 'room-join row',
              onsubmit: (e) => {
                e.preventDefault();
                a.joinRoom();
              },
            },
            h('label', { for: 'room-code' }, 'Room code'),
            h('input', {
              id: 'room-code',
              type: 'text',
              value: m.roomCode,
              maxlength: 6,
              placeholder: 'ABCD',
              autocomplete: 'off',
              autocapitalize: 'characters',
              spellcheck: 'false',
              oninput: (e) => a.setRoomCode((e.target as HTMLInputElement).value),
            }),
            h(
              'button',
              { class: 'btn primary', type: 'submit' },
              m.roomCode ? `Join room ${m.roomCode.toUpperCase()}` : 'Join room',
            ),
          ),
          m.roomError ? h('p', { class: 'auth-error', role: 'alert' }, m.roomError) : null,
          h('h2', {}, 'Play on this device'),
          h(
            'div',
            { class: 'row' },
            h(
              'button',
              {
                class: 'btn blue',
                disabled: !PLAYABLE_PROGRAMMES.has('full'),
                title: PLAYABLE_PROGRAMMES.has('full') ? '' : 'Coming when all ten events are in',
                onclick: () => a.practice('full'),
              },
              '🏅 Full decathlon',
            ),
            h(
              'button',
              {
                class: 'btn ghost',
                disabled: !PLAYABLE_PROGRAMMES.has('day1'),
                onclick: () => a.practice('day1'),
              },
              '1️⃣ Day 1 (5 events)',
            ),
            h(
              'button',
              {
                class: 'btn ghost',
                disabled: !PLAYABLE_PROGRAMMES.has('day2'),
                title: 'The five Day 2 events',
                onclick: () => a.practice('day2'),
              },
              '2️⃣ Day 2',
            ),
          ),
          h(
            'div',
            { class: 'row', style: 'justify-content:space-between' },
            h('h3', { style: 'margin:0' }, 'Practice one event'),
            h(
              'div',
              { class: 'toggle' },
              h(
                'button',
                {
                  class: m.practiceBots ? 'on' : '',
                  'aria-pressed': String(m.practiceBots),
                  onclick: () => a.setPracticeBots(true),
                },
                '🤖 vs bots',
              ),
              h(
                'button',
                {
                  class: m.practiceBots ? '' : 'on',
                  'aria-pressed': String(!m.practiceBots),
                  onclick: () => a.setPracticeBots(false),
                },
                'Solo',
              ),
            ),
          ),
          day(1),
          day(2),
          h(
            'div',
            { class: 'row', style: 'justify-content:space-between' },
            h('button', { class: 'btn small ghost', onclick: () => a.about() }, 'ℹ️ About'),
            h('button', { class: 'btn small ghost', onclick: () => a.showLeague() }, '🏆 League'),
          ),
        ),
        h(
          'div',
          { class: 'panel stack' },
          h('h2', {}, 'Settings'),
          h('label', {}, 'Kit colour'),
          h(
            'div',
            { class: 'swatches' },
            KIT_COLOURS.map((c) =>
              h('button', {
                class: `swatch${c.hex === m.kit ? ' selected' : ''}`,
                style: `background:${c.hex}`,
                title: c.name,
                'aria-label': c.name,
                'aria-pressed': String(c.hex === m.kit),
                onclick: () => a.setKit(c.hex),
              }),
            ),
          ),
          h('label', {}, 'Run keys'),
          h(
            'div',
            { class: 'difficulty' },
            BINDINGS.map((b) =>
              h(
                'button',
                {
                  class: `diff-btn${b.id === m.binding.id ? ' selected' : ''}`,
                  'aria-pressed': String(b.id === m.binding.id),
                  onclick: () => a.setBinding(b.id),
                },
                h(
                  'span',
                  { class: 'keys' },
                  h('kbd', {}, keyLabel(b.left)),
                  h('kbd', {}, keyLabel(b.right)),
                ),
                h('span', {}, b.blurb),
              ),
            ),
          ),
          h(
            'button',
            {
              class: 'btn ghost',
              'aria-pressed': String(m.binding.id === 'custom'),
              onclick: (event: Event) => {
                const button = event.currentTarget as HTMLButtonElement;
                const keys: string[] = [];
                button.textContent = 'Press your first run key (Esc cancels)';
                button.focus();
                const capture = (e: KeyboardEvent) => {
                  e.preventDefault();
                  e.stopPropagation();
                  if (e.code === 'Escape') {
                    button.removeEventListener('keydown', capture);
                    button.textContent = 'Custom run keys';
                    return;
                  }
                  if (e.repeat || !validRunCode(e.code) || keys.includes(e.code)) return;
                  keys.push(e.code);
                  if (keys.length === 1) button.textContent = 'Press your second run key';
                  else {
                    button.removeEventListener('keydown', capture);
                    if (saveCustomBinding(keys[0]!, keys[1]!)) a.setBinding('custom');
                  }
                };
                button.addEventListener('keydown', capture);
              },
            },
            m.binding.id === 'custom'
              ? `Custom: ${keyLabel(m.binding.left)} / ${keyLabel(m.binding.right)}`
              : 'Custom run keys',
          ),
          h(
            'p',
            { class: 'muted small' },
            'Space: press to take off, hold for the angle gauge, release to fly. Use the camera button to change views. Tab moves between controls; Esc quits.',
          ),
          h('label', {}, 'Camera'),
          h(
            'div',
            { class: 'toggle' },
            VIEW_MODES.map((v) =>
              h(
                'button',
                {
                  class: v.id === m.view ? 'on' : '',
                  'aria-pressed': String(v.id === m.view),
                  onclick: () => a.setView(v.id),
                },
                `${v.icon} ${v.name}`,
              ),
            ),
          ),
          h('label', {}, 'Sound'),
          h(
            'div',
            { class: 'row' },
            h(
              'div',
              { class: 'toggle' },
              h(
                'button',
                {
                  class: m.sfxOn ? 'on' : '',
                  'aria-pressed': String(m.sfxOn),
                  onclick: () => a.setSfx(true),
                },
                '🔊 Effects',
              ),
              h(
                'button',
                {
                  class: m.sfxOn ? '' : 'on',
                  'aria-pressed': String(!m.sfxOn),
                  onclick: () => a.setSfx(false),
                },
                '🔇 Off',
              ),
            ),
            h(
              'div',
              { class: 'toggle' },
              h(
                'button',
                {
                  class: m.paOn ? 'on' : '',
                  'aria-pressed': String(m.paOn),
                  title: 'Stadium announcer (speech synthesis)',
                  onclick: () => a.setPa(true),
                },
                '📣 Announcer',
              ),
              h(
                'button',
                {
                  class: m.paOn ? '' : 'on',
                  'aria-pressed': String(!m.paOn),
                  onclick: () => a.setPa(false),
                },
                'Off',
              ),
            ),
            h(
              'div',
              { class: 'toggle' },
              h(
                'button',
                {
                  class: m.musicOn ? '' : 'on',
                  'aria-pressed': String(!m.musicOn),
                  onclick: () => a.setMusicOn(false),
                },
                'Music off',
              ),
              MUSIC_TRACKS.map((t) =>
                h(
                  'button',
                  {
                    class: m.musicOn && m.music === t.id ? 'on' : '',
                    'aria-pressed': String(m.musicOn && m.music === t.id),
                    title: t.blurb,
                    onclick: () => (a.setMusic(t.id), a.setMusicOn(true)),
                  },
                  t.name,
                ),
              ),
            ),
          ),
        ),
      ),
    ),
  );
  if (!m.account && m.authMode === 'none' && !m.name)
    setTimeout(() => {
      if (input.isConnected) input.focus();
    }, 0);
}
